import {throwError as observableThrowError,  Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';
import {JsonService} from "../../../services/core/json.service";
import {ServiceApiResources} from "./service.constants";

@Injectable({
  providedIn: 'root'
})
export class ServiceDataService {
  private baseURL;

  // Subject for subcontractor list refresh
  private subcontractorListRefresh = new Subject<boolean>();
  subcontractorListRefresh$ = this.subcontractorListRefresh.asObservable();

  // Method to trigger subcontractor list refresh
  triggerSubcontractorRefresh(): void {
    this.subcontractorListRefresh.next(true);
  }

  constructor(
    private http: HttpClient,
    private jsonService: JsonService,
  ) {
    this.baseURL = ServiceApiResources;
  }

  list(model: any = null): Observable<any> {
    const endPoint = `${this.baseURL.list}`;
    return this.http.post<any>(endPoint, model)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  create(model: any): Observable<any> {
    const endPoint = `${this.baseURL.create}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // detail(id:any): Observable<any> {
  //   const endPoint = `${this.baseURL.detail}/${id}`;
  //   return this.http.get<any>(endPoint)
  //     .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  // }

  detail(id: any, params?: any): Observable<any> {
    const endPoint = `${this.baseURL.detail}/${id}`;
    return this.http.get<any>(endPoint, { params }) 
      .pipe(
        tap(data => this.jsonService.doStringify(data)), 
        catchError(this.errorHandlerMessage)
      );
  }  

  update(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.update}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  addSubcontractor(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.addSubcontractor}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  copyService(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.copy}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  delete(id:any): Observable<any> {
    const endPoint = `${this.baseURL.delete}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Bulk delete: DELETE services/delete/multiple with { service_ids: [...] }
  deleteMultiple(serviceIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteMultiple}`;
    return this.http.request<any>('delete', endPoint, { body: { service_ids: serviceIds } })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Pre-check which of the selected services can be deleted (and why not, for the rest)
  deleteAvailability(serviceIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteAvailability}`;
    return this.http.post<any>(endPoint, { service_ids: serviceIds })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  deleteSubcontractor(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteSubcontractor}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  getTypes(): Observable<any> {
    const endPoint = `${this.baseURL.types}`;
    return this.http.get<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  subcontractorStatus(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.subcontractorStatusChange}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  changeAttribute(model: any): Observable<any> {
    const endPoint = `${this.baseURL.changeAttribute}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  onChangeStatus(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.changeStatus}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  // Import Methods
  downloadSampleFile(): Observable<any> {
    const endPoint = `${this.baseURL.downloadSample}`;
    return this.http.get(endPoint, { responseType: 'blob' })
      .pipe(catchError(this.errorHandlerMessage));
  }

  uploadImportFile(file: File): Observable<any> {
    const endPoint = `${this.baseURL.uploadImport}`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(endPoint, formData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  validateImportData(data: any): Observable<any> {
    const endPoint = `${this.baseURL.validateImport}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  executeImport(data: any): Observable<any> {
    const endPoint = `${this.baseURL.executeImport}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportJobStatus(jobId: string): Observable<any> {
    const endPoint = `${this.baseURL.importStatus}/${jobId}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // Import History Methods (reuse existing with module_id=11)
  getImportHistory(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}`;
    let httpParams = new HttpParams();
    
    // Always add service module_id
    httpParams = httpParams.set('module_id', '11');
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });
    
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportHistoryDetails(id: number): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/${id}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  deleteImportHistory(id: number): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportStats(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/stats`;
    let httpParams = new HttpParams();
    
    // Always add service module_id
    httpParams = httpParams.set('module_id', '11');
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });
    
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }
}
